import { authService, type User } from '@/services/auth'
import { showToast } from './toast'
import { ApiService } from '@/services/api'

export class UserProfile {
  private modal: HTMLElement | null = null
  private user: User | null = null

  constructor(user: User) {
    this.user = user
  }

  show(): void {
    this.createModal()
    this.attachEventListeners()
    // Trigger a fresh health check when profile opens
    this.triggerHealthCheck()
  }

  hide(): void {
    if (this.modal) {
      this.modal.remove()
      this.modal = null
    }
    // Clear the current profile reference when hiding
    if (currentProfile === this) {
      currentProfile = null
    }
  }

  private createModal(): void {
    // Remove existing modal if any
    this.hide()

    this.modal = document.createElement('div')
    this.modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
    this.modal.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div class="p-6">
          <!-- Header -->
          <div class="flex justify-between items-center mb-6">
            <h2 class="text-2xl font-bold text-gray-900">Profile</h2>
            <button id="profile-modal-close" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <!-- User Info -->
          <div class="mb-6">
            <div class="flex items-center mb-4">
              <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <span class="text-blue-600 font-semibold text-lg">
                  ${this.user?.display_name ? this.user.display_name.charAt(0).toUpperCase() : this.user?.email.charAt(0).toUpperCase()}
                </span>
              </div>
              <div class="ml-3">
                <h3 class="text-lg font-medium text-gray-900">
                  ${this.user?.display_name || 'User'}
                </h3>
                <p class="text-sm text-gray-500">${this.user?.email}</p>
              </div>
            </div>
            
            <div class="text-sm text-gray-600 space-y-1">
              <p><span class="font-medium">Member since:</span> ${(() => {
                const dateStr = this.user?.createdAt || this.user?.created_at;
                return dateStr ? new Date(dateStr).toLocaleDateString() : 'Unknown';
              })()}</p>
              ${this.user?.last_login ? `<p><span class="font-medium">Last login:</span> ${new Date(this.user.last_login).toLocaleDateString()}</p>` : ''}
              <div class="flex items-center mt-2">
                <span class="font-medium mr-2">Connection:</span>
                <span id="profile-connection-status" class="flex items-center text-sm">
                  <span class="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                  Checking...
                </span>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="space-y-3">
            <button
              id="change-password-btn"
              class="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              Change Password
            </button>
            
            <button
              id="logout-btn"
              class="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
            >
              Sign Out
            </button>
          </div>

          <!-- Change Password Form (Hidden by default) -->
          <div id="change-password-form" class="hidden mt-6 pt-6 border-t border-gray-200">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
            <form id="password-form" class="space-y-4">
              <div>
                <label for="current-password" class="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  id="current-password"
                  name="current_password"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label for="new-password" class="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  id="new-password"
                  name="new_password"
                  required
                  minlength="8"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label for="confirm-password" class="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirm-password"
                  name="confirm_password"
                  required
                  minlength="8"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div class="flex space-x-3">
                <button
                  type="submit"
                  id="password-submit"
                  class="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  Update Password
                </button>
                <button
                  type="button"
                  id="cancel-password"
                  class="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    `

    document.body.appendChild(this.modal)
  }

  private attachEventListeners(): void {
    if (!this.modal) return

    // Close modal
    const closeBtn = this.modal.querySelector('#profile-modal-close')
    closeBtn?.addEventListener('click', () => this.hide())

    // Close on backdrop click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.hide()
      }
    })

    // Change password button
    const changePasswordBtn = this.modal.querySelector('#change-password-btn')
    changePasswordBtn?.addEventListener('click', () => {
      const form = this.modal?.querySelector('#change-password-form') as HTMLElement
      form?.classList.remove('hidden')
    })

    // Cancel password change
    const cancelPasswordBtn = this.modal.querySelector('#cancel-password')
    cancelPasswordBtn?.addEventListener('click', () => {
      const form = this.modal?.querySelector('#change-password-form') as HTMLElement
      form?.classList.add('hidden')
      this.resetPasswordForm()
    })

    // Password form submission
    const passwordForm = this.modal.querySelector('#password-form') as HTMLFormElement
    passwordForm?.addEventListener('submit', (e) => {
      e.preventDefault()
      this.handlePasswordChange()
    })

    // Logout button
    const logoutBtn = this.modal.querySelector('#logout-btn')
    console.log('🔴 LOGOUT: Button found:', logoutBtn)
    logoutBtn?.addEventListener('click', () => {
      console.log('🔴 LOGOUT: Button clicked!')
      this.handleLogout()
    })
  }

  private async handlePasswordChange(): Promise<void> {
    if (!this.modal) return

    const form = this.modal.querySelector('#password-form') as HTMLFormElement
    const formData = new FormData(form)
    
    const currentPassword = formData.get('current_password') as string
    const newPassword = formData.get('new_password') as string
    const confirmPassword = formData.get('confirm_password') as string

    if (!currentPassword || !newPassword || !confirmPassword) {
      showToast('Please fill in all fields', 'error')
      return
    }

    if (newPassword.length < 8) {
      showToast('New password must be at least 8 characters long', 'error')
      return
    }

    if (newPassword !== confirmPassword) {
      showToast('New passwords do not match', 'error')
      return
    }

    this.setPasswordLoading(true)

    try {
      await authService.changePassword(currentPassword, newPassword)
      showToast('Password updated successfully!', 'success')
      
      const form = this.modal?.querySelector('#change-password-form') as HTMLElement
      form?.classList.add('hidden')
      this.resetPasswordForm()
    } catch (error: any) {
      console.error('Password change error:', error)
      
      let errorMessage = 'Failed to update password. Please try again.'
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error.message) {
        errorMessage = error.message
      }
      
      showToast(errorMessage, 'error')
    } finally {
      this.setPasswordLoading(false)
    }
  }

  private async handleLogout(): Promise<void> {
    console.log('🔴 LOGOUT: handleLogout() called!')
    
    try {
      // Use Better Auth's signOut method
      await authService.signOut()
      
      showToast('Signed out successfully', 'success')
    } catch (error) {
      console.error('Logout error:', error)
      showToast('Signing out...', 'info')
    } finally {
      // Hide modal
      this.hide()
      
      // Redirect to login page
      console.log('🔴 LOGOUT: Redirecting to login page...')
      window.location.href = '/'
    }
  }

  private setPasswordLoading(loading: boolean): void {
    if (!this.modal) return

    const submitBtn = this.modal.querySelector('#password-submit') as HTMLButtonElement
    const cancelBtn = this.modal.querySelector('#cancel-password') as HTMLButtonElement
    const form = this.modal.querySelector('#password-form') as HTMLFormElement

    if (loading) {
      submitBtn.disabled = true
      submitBtn.textContent = 'Updating...'
      cancelBtn.disabled = true
      form.style.opacity = '0.6'
    } else {
      submitBtn.disabled = false
      submitBtn.textContent = 'Update Password'
      cancelBtn.disabled = false
      form.style.opacity = '1'
    }
  }

  private resetPasswordForm(): void {
    if (!this.modal) return

    const form = this.modal.querySelector('#password-form') as HTMLFormElement
    form.reset()
  }

  public updateConnectionStatus(isConnected: boolean): void {
    if (!this.modal) return

    const statusElement = this.modal.querySelector('#profile-connection-status')
    if (statusElement) {
      if (isConnected) {
        statusElement.innerHTML = `
          <span class="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
          Connected
        `
      } else {
        statusElement.innerHTML = `
          <span class="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
          Disconnected
        `
      }
    }
  }

  private triggerHealthCheck(): void {
    // Show checking state
    const statusElement = this.modal?.querySelector('#profile-connection-status')
    if (statusElement) {
      statusElement.innerHTML = `
        <span class="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
        Checking...
      `
    }

    // Trigger the main app's health check by calling the API service directly
    this.checkBackendConnection()
  }

  private async checkBackendConnection(): Promise<void> {
    if (!this.modal) return

    try {
      const apiService = new ApiService()
      console.log('🔍 Checking backend connection...')
      const health = await apiService.getHealth()
      console.log('🔍 Health check response:', health)
      
      if (health.status === 'ok') {
        console.log('✅ Backend connection successful')
        this.updateConnectionStatus(true)
      } else {
        console.log('⚠️ Backend reported unhealthy status:', health.status)
        this.updateConnectionStatus(false)
      }
    } catch (error) {
      console.error('❌ Backend connection check failed:', error)
      this.updateConnectionStatus(false)
    }
  }
}

// Store the current profile instance so we can update connection status
let currentProfile: UserProfile | null = null

// Export a function to show the user profile
export function showUserProfile(user: User): UserProfile {
  const profile = new UserProfile(user)
  currentProfile = profile
  profile.show()
  return profile
}

// Export a function to update connection status in the profile modal
export function updateProfileConnectionStatus(isConnected: boolean): void {
  if (currentProfile) {
    currentProfile.updateConnectionStatus(isConnected)
  }
}
